/**
 * Operator vocabulary for the `user_is` workflow condition.
 *
 * XPEN-3348. Mirrors `RulesProcessor.USER_IS_NEGATING_OPERATORS` in xpens-api
 * (`src/utils/rules_processor.py`, added by XPEN-3347 — search that constant
 * name). The step editor and the step summary card both read this, so the two
 * screens can never describe the same stored condition differently, nor
 * differently from the engine that evaluates it. **If the backend set changes,
 * this must change with it** — nothing enforces that automatically.
 *
 * `not_exists` is deliberately absent, matching the backend: there it asks
 * "is the field missing", which is a different question from "is the user not
 * one of these".
 */
export const USER_IS_NEGATING_OPERATORS = ["not_equal", "different", "not_contain"];

/**
 * True when a stored operator makes a `user_is` condition exclude the selected
 * users. The operator is never validated on save, so the stored value can be any
 * JSON type.
 *
 * Strings are lower-cased and trimmed, as the backend does before comparing.
 * (Exactly equivalent for every whitespace character reachable in practice;
 * JavaScript's `trim` and Python's `strip` disagree only on U+FEFF and U+0085.)
 *
 * Non-strings are rejected rather than coerced: `String(["different"])` is
 * "different" in JavaScript but `str(["different"])` is "['different']" in
 * Python, so coercing here would make this screen read "is not" for a value the
 * engine evaluates as "is".
 *
 * This resolves for display only. An unrecognised stored operator is shown as
 * "is" but left untouched in the document, so nothing is silently rewritten by
 * opening a rule.
 */
export const isNegatingUserOperator = (operator) =>
    typeof operator === "string"
    && USER_IS_NEGATING_OPERATORS.includes(operator.toLowerCase().trim());
